import React, { useEffect, useState } from "react";
import { ADMIN_ENDPOINTS, API_ENDPOINTS } from "../config/constants";
import {
  getVehicleMakes,
  addVehicleMake,
  updateVehicleMake,
  addVehicleModel,
  updateVehicleModel,
  deleteVehicleModel,
} from "../services/vehicleMakes";

const AdminDashboard = () => {
  const [pendingImages, setPendingImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [reportedImages, setReportedImages] = useState([]);
  const [loadingReported, setLoadingReported] = useState(true);
  const [errorReported, setErrorReported] = useState(null);

  const [reportedComments, setReportedComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [errorComments, setErrorComments] = useState(null);

  const [message, setMessage] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);

  // Add new state for vehicle makes management
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(true);
  const [errorMakes, setErrorMakes] = useState(null);
  const [selectedMake, setSelectedMake] = useState(null);
  const [newMake, setNewMake] = useState({ name: "", category: "" });
  const [newModel, setNewModel] = useState({ name: "" });

  // Add categories array
  const categories = [
    "European",
    "American",
    "Japanese",
    "Korean",
    "Malaysian",
    "Chinese",
    "Luxury",
    "Classic",
  ];

  // Add state for streak freeze management
  const [streakFreezeUserId, setStreakFreezeUserId] = useState("");
  const [streakFreezeAction, setStreakFreezeAction] = useState("add");
  const [streakFreezeCount, setStreakFreezeCount] = useState(1);
  const [streakFreezeMessage, setStreakFreezeMessage] = useState(null);
  const [isLoadingStreakFreeze, setIsLoadingStreakFreeze] = useState(false);

  // Fetch pending images and reported comments on mount
  useEffect(() => {
    // Fetch pending images
    fetch(ADMIN_ENDPOINTS.GET_PENDING_IMAGES, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch pending images");
        return res.json();
      })
      .then((data) => {
        setPendingImages(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching pending images", err);
        setError("Failed to load pending images.");
        setLoading(false);
      });

    // Fetch reported comments
    fetch(API_ENDPOINTS.GET_REPORTED_COMMENTS, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch reported comments");
        return res.json();
      })
      .then((data) => {
        setReportedComments(Array.isArray(data) ? data : []);
        setLoadingComments(false);
      })
      .catch((err) => {
        console.error("Error fetching reported comments", err);
        setErrorComments("Failed to load reported comments.");
        setLoadingComments(false);
      });
  }, []);

  // Fetch reported images
  useEffect(() => {
    fetch(ADMIN_ENDPOINTS.GET_REPORTED_IMAGES, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch reported images");
        return res.json();
      })
      .then((data) => {
        setReportedImages(data);
        setLoadingReported(false);
      })
      .catch((err) => {
        console.error("Error fetching reported images", err);
        setErrorReported("Failed to load reported images.");
        setLoadingReported(false);
      });
  }, []);

  // Add new useEffect for fetching vehicle makes
  useEffect(() => {
    fetchVehicleMakes();
  }, []);

  const fetchVehicleMakes = async () => {
    try {
      setLoadingMakes(true);
      const makes = await getVehicleMakes();
      setVehicleMakes(makes);
      setErrorMakes(null);
      return makes;
    } catch (err) {
      console.error("Error fetching vehicle makes:", err);
      setErrorMakes("Failed to load vehicle makes");
      throw err;
    } finally {
      setLoadingMakes(false);
    }
  };

  const handleAddMake = async (e) => {
    e.preventDefault();
    try {
      await addVehicleMake(newMake);
      await fetchVehicleMakes();
      setNewMake({ name: "", category: "" });
      setMessage({ text: "Make added successfully!", type: "success" });
    } catch (err) {
      setMessage({ text: `Error adding make: ${err.message}`, type: "error" });
    }
  };

  const handleAddModel = async (e) => {
    e.preventDefault();
    if (!selectedMake) return;

    try {
      await addVehicleModel(selectedMake._id, newModel);
      const makes = await getVehicleMakes();
      setVehicleMakes(makes);
      const updatedSelectedMake = makes.find(
        (make) => make._id === selectedMake._id
      );
      setSelectedMake(updatedSelectedMake);
      setNewModel({ name: "" });

      setMessage({ text: "Model added successfully!", type: "success" });
    } catch (err) {
      setMessage({ text: `Error adding model: ${err.message}`, type: "error" });
    }
  };

  const handleUpdateMake = async (makeId, updates) => {
    try {
      await updateVehicleMake(makeId, updates);
      await fetchVehicleMakes();
      setMessage({ text: "Make updated successfully!", type: "success" });
    } catch (err) {
      setMessage({
        text: `Error updating make: ${err.message}`,
        type: "error",
      });
    }
  };

  const handleDeleteModel = async (makeId, modelId) => {
    if (!window.confirm("Are you sure you want to delete this model?")) return;

    try {
      await deleteVehicleModel(makeId, modelId);
      await fetchVehicleMakes();

      // remove the model from vehicleMakes
      setSelectedMake((prev) => ({
        ...prev,
        models: prev.models.filter((m) => m._id !== modelId),
      }));

      setMessage({ text: "Model deleted successfully!", type: "success" });
    } catch (err) {
      setMessage({
        text: `Error deleting model: ${err.message}`,
        type: "error",
      });
    }
  };

  const handleApprove = async (vehicleId, imageId) => {
    try {
      setLoadingPending(true);
      const res = await fetch(
        ADMIN_ENDPOINTS.APPROVE_IMAGE(vehicleId, imageId),
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to approve image");

      // Remove the approved image from pending list
      setPendingImages((prev) => prev.filter((img) => img.id !== imageId));
      setMessage({ text: "Image approved successfully!", type: "success" });
    } catch (err) {
      console.error("Error approving image:", err);
      setMessage({ text: "Error approving image", type: "error" });
    } finally {
      setLoadingPending(false);
    }
  };

  const handleReject = async (vehicleId, imageId) => {
    try {
      setLoadingPending(true);
      const res = await fetch(
        ADMIN_ENDPOINTS.REJECT_IMAGE(vehicleId, imageId),
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to reject image");

      // Remove the rejected image from pending list
      setPendingImages((prev) => prev.filter((img) => img.id !== imageId));
      setMessage({ text: "Image rejected successfully!", type: "success" });
    } catch (err) {
      console.error("Error rejecting image:", err);
      setMessage({ text: "Error rejecting image", type: "error" });
    } finally {
      setLoadingPending(false);
    }
  };

  const handleModerateComment = async (commentId, action) => {
    try {
      const res = await fetch(API_ENDPOINTS.MODERATE_COMMENT(commentId), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.msg || "Error moderating comment");
      }

      setReportedComments((prev) =>
        prev.filter((comment) => comment._id !== commentId)
      );
    } catch (err) {
      console.error("Error moderating comment", err);
      alert(err.message || "Error moderating comment");
    }
  };

  // Add new function for moderating reported images
  const handleModerateImage = async (vehicleId, imageId, action) => {
    try {
      const res = await fetch(
        ADMIN_ENDPOINTS.MODERATE_IMAGE(vehicleId, imageId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ action }),
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to moderate image");
      }

      // Remove the moderated image from the list
      setReportedImages((prev) => prev.filter((img) => img.id !== imageId));
      setMessage({
        text: `Image ${
          action === "hide" ? "hidden" : "approved"
        } successfully!`,
        type: "success",
      });
    } catch (err) {
      console.error("Error moderating image:", err);
      setMessage({
        text: `Error ${action === "hide" ? "hiding" : "approving"} image: ${
          err.message
        }`,
        type: "error",
      });
    }
  };

  const handleManageStreakFreeze = async (e) => {
    e.preventDefault();
    if (!streakFreezeUserId) {
      setStreakFreezeMessage({
        text: "Username or User ID is required",
        type: "error",
      });
      return;
    }

    setIsLoadingStreakFreeze(true);
    setStreakFreezeMessage(null);

    try {
      const res = await fetch(
        ADMIN_ENDPOINTS.MANAGE_STREAK_FREEZES(streakFreezeUserId),
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: streakFreezeAction,
            count: parseInt(streakFreezeCount, 10),
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to manage streak freezes");
      }

      // Try to parse the response, but don't fail if there's no JSON content
      let data;
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (e) {
          // Ignore JSON parsing errors in success case
          console.log("No JSON in response body");
        }
      }

      setStreakFreezeMessage({
        text: `Streak freeze ${streakFreezeAction}ed successfully for ${streakFreezeUserId}!`,
        type: "success",
      });

      // Reset the form after successful submission
      setStreakFreezeUserId("");
      setStreakFreezeCount(1);
    } catch (err) {
      console.error("Error managing streak freezes:", err);
      setStreakFreezeMessage({
        text:
          err.message ||
          "Error managing streak freezes. Please check the username or ID is correct.",
        type: "error",
      });
    } finally {
      setIsLoadingStreakFreeze(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>

      {message && (
        <div
          className={`mb-4 p-2 rounded ${
            message.type === "success"
              ? "bg-green-100 dark:bg-green-900 dark:text-green-100"
              : "bg-red-100 dark:bg-red-900 dark:text-red-100"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Add Streak Freeze Management Section */}
      <div className="mb-8 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4">Manage Streak Freezes</h2>

        {streakFreezeMessage && (
          <div
            className={`mb-4 p-2 rounded ${
              streakFreezeMessage.type === "success"
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
            }`}
          >
            {streakFreezeMessage.text}
          </div>
        )}

        <form onSubmit={handleManageStreakFreeze} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
              Username or User ID
            </label>
            <input
              type="text"
              value={streakFreezeUserId}
              onChange={(e) => setStreakFreezeUserId(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter username or user ID"
              required
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              You can enter either a username (e.g., "ftwYan") or user ID (e.g.,
              "6094c7d8a87d3f001c8e9a7b")
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
              Action
            </label>
            <select
              value={streakFreezeAction}
              onChange={(e) => setStreakFreezeAction(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="add">Add Freezes</option>
              <option value="remove">Remove Freezes</option>
              <option value="reset">Reset Freezes</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
              Count
            </label>
            <input
              type="number"
              min="1"
              value={streakFreezeCount}
              onChange={(e) => setStreakFreezeCount(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={isLoadingStreakFreeze}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoadingStreakFreeze ? "Processing..." : "Submit"}
          </button>
        </form>
      </div>

      {/* Vehicle Makes Management Section */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Vehicle Makes Management</h3>
        {loadingMakes ? (
          <div>Loading vehicle makes...</div>
        ) : errorMakes ? (
          <div className="text-red-600">{errorMakes}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Add New Make Form */}
            <div className="border rounded-lg p-4">
              <h4 className="text-lg font-medium mb-3">Add New Make</h4>
              <form onSubmit={handleAddMake} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newMake.name}
                    onChange={(e) =>
                      setNewMake({ ...newMake, name: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    value={newMake.category}
                    onChange={(e) =>
                      setNewMake({ ...newMake, category: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Add Make
                </button>
              </form>
            </div>

            {/* Makes List and Models Management */}
            <div className="border rounded-lg p-4">
              <h4 className="text-lg font-medium mb-3">
                Manage Makes and Models
              </h4>
              <div className="space-y-4">
                <select
                  value={selectedMake?._id || ""}
                  onChange={(e) =>
                    setSelectedMake(
                      vehicleMakes.find((m) => m._id === e.target.value)
                    )
                  }
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select a Make</option>
                  {vehicleMakes.map((make) => (
                    <option key={make._id} value={make._id}>
                      {make.name}
                    </option>
                  ))}
                </select>

                {selectedMake && (
                  <div className="space-y-4">
                    <div className="border-t pt-4">
                      <h5 className="font-medium mb-2">Add New Model</h5>
                      <form onSubmit={handleAddModel} className="flex gap-2">
                        <input
                          type="text"
                          value={newModel.name}
                          onChange={(e) =>
                            setNewModel({ name: e.target.value })
                          }
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                          placeholder="Model name"
                          required
                        />
                        <button
                          type="submit"
                          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
                        >
                          Add
                        </button>
                      </form>
                    </div>

                    <div className="border-t pt-4">
                      <h5 className="font-medium mb-2">Current Models</h5>
                      <div className="space-y-2">
                        {selectedMake.models.map((model) => (
                          <div
                            key={model._id}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded dark:bg-gray-800 dark:text-gray-400"
                          >
                            <span>{model.name}</span>
                            <button
                              onClick={() =>
                                handleDeleteModel(selectedMake._id, model._id)
                              }
                              className="text-red-500 hover:text-red-700 dark:text-red-500 dark:hover:text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Pending Images</h3>
        {loadingPending ? (
          <div>Loading pending images...</div>
        ) : pendingImages.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingImages.map((image) => (
              <div
                key={image.id}
                className="border rounded-lg p-4 dark:border-gray-700 dark:text-gray-400"
              >
                <img
                  src={image.url}
                  alt="Pending approval"
                  className="w-full h-48 object-cover mb-2 rounded"
                />
                <div className="text-sm text-gray-600 mb-2 dark:text-gray-400">
                  <div>Plate: {image.plate}</div>
                  <div>Submitted by: {image.submittedBy}</div>
                  <div>
                    Submitted: {new Date(image.submittedAt).toLocaleString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(image.vehicleId, image.id)}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 flex-1 dark:bg-green-600 dark:hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(image.vehicleId, image.id)}
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 flex-1 dark:bg-red-600 dark:hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-500">No pending images</div>
        )}
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Reported Images</h3>
        {loadingReported ? (
          <div>Loading reported images...</div>
        ) : reportedImages.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reportedImages.map((report) => (
              <div
                key={report.id}
                className="border rounded-lg p-4 dark:border-gray-700 dark:text-gray-400"
              >
                <picture>
                  {report.variants?.webp?.medium && (
                    <source
                      srcSet={report.variants.webp.medium}
                      type="image/webp"
                    />
                  )}
                  <img
                    src={report.variants?.medium || report.url}
                    alt="Reported"
                    className="w-full h-48 object-cover mb-2 rounded"
                  />
                </picture>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div>Plate: {report.plate}</div>
                  <div className="font-semibold text-red-600 dark:text-red-400">
                    {report.reports?.length || 0} Report
                    {report.reports?.length !== 1 ? "s" : ""}
                    {report.isHidden && " - Hidden"}
                  </div>
                  <div className="mt-2 mb-2">
                    <strong>Reports:</strong>
                    <ul className="ml-4 list-disc">
                      {report.reports?.map((r, idx) => (
                        <li key={idx}>
                          {r.reason}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            - by {r.user}
                            {r.reportedAt &&
                              ` on ${new Date(
                                r.reportedAt
                              ).toLocaleDateString()}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() =>
                        handleModerateImage(
                          report.vehicleId,
                          report.id,
                          "approve"
                        )
                      }
                      className="flex-1 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
                    >
                      Approve & Clear Reports
                    </button>
                    <button
                      onClick={() =>
                        handleModerateImage(report.vehicleId, report.id, "hide")
                      }
                      className="flex-1 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700"
                    >
                      Hide Image
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-500">No reported images</div>
        )}
      </div>

      <h2 className="text-xl font-bold mb-4">Reported Comments</h2>
      {loadingComments ? (
        <div>Loading reported comments...</div>
      ) : errorComments ? (
        <div className="text-red-600">{errorComments}</div>
      ) : reportedComments.length === 0 ? (
        <div>No reported comments.</div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className="px-4 py-2">Comment</th>
              <th className="px-4 py-2">Vehicle</th>
              <th className="px-4 py-2">Author</th>
              <th className="px-4 py-2">Reports</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reportedComments.map((comment) => (
              <tr key={comment._id} className="border-b dark:border-gray-700">
                <td className="px-4 py-2">{comment.comment}</td>
                <td className="px-4 py-2">
                  {comment.vehicle?.plate || "Unknown"}
                </td>
                <td className="px-4 py-2">
                  {comment.user?.username || "Anonymous"}
                </td>
                <td className="px-4 py-2">
                  <details>
                    <summary>{comment.reports.length} reports</summary>
                    <ul className="text-sm">
                      {comment.reports.map((report, idx) => (
                        <li key={idx}>
                          {report.user?.username}:{" "}
                          {report.reason || "No reason provided"}
                        </li>
                      ))}
                    </ul>
                  </details>
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={() =>
                      handleModerateComment(comment._id, "approve")
                    }
                    className="mr-2 bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleModerateComment(comment._id, "reject")}
                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700"
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminDashboard;
